'use client'

import { forwardRef } from 'react'
import clsx from 'clsx'
import { usePathname, useRouter } from '@/lib/next-shim'
import { useLocale, useTranslations } from '@/lib/next-shim'

import { Button, Dialog, DialogContent, DialogTitle, IconButton, Slide, Stack } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'

import { languages, SupportedLanguage } from '@/lib/i18n/settings'
import { useAuth } from '@/shared/ui/providers/AuthSessionProvider'
import { useUpdateMyInfosMutation } from '@/core/graphql/hooks'

export type LanguageSelectionDialogProps = {
  fullScreen?: boolean
  onClose?: () => void
  open: boolean
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />
})

export function LanguageSelectionDialog(props: LanguageSelectionDialogProps) {
  const { fullScreen = false, onClose, open } = props
  const lang = useLocale()
  const pathname = usePathname()
  const router = useRouter()
  const t = useTranslations()
  const { isAuthenticated } = useAuth()
  const [updateMyInfos] = useUpdateMyInfosMutation()

  // Handler to persist language preference to backend when user is authenticated
  const handleLanguageSelect = async (selectedLang: SupportedLanguage) => {
    if (isAuthenticated && selectedLang !== lang) {
      try {
        await updateMyInfos({
          variables: {
            payload: { language: selectedLang },
          },
        })
      } catch (error) {
        console.error('Failed to update language preference:', error)
      }
    }

    router.push(pathname ? `/${selectedLang}${pathname}` : `/${selectedLang}`)
    onClose?.()
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      scroll="paper"
      TransitionComponent={fullScreen ? Transition : undefined}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {t('dialog.LanguageSelection.title')}
          <IconButton onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack>
          <div>{t('dialog.LanguageSelection.content')}</div>
          <Stack direction="row" flexWrap="wrap">
            {languages.map((lng) => (
              <Button
                key={lng}
                data-testid={`lang-option-${lng}`}
                onClick={() => handleLanguageSelect(lng)}
                className={clsx('flex justify-between w-full md:w-1/4 text-sm font-normal', {
                  'text-black': lng !== lang,
                  'pointer-events-none': lng === lang,
                })}
              >
                {t(`language.${lng}`)}
                <CheckRoundedIcon className={clsx('md:hidden', { 'opacity-0': lng !== lang })} />
              </Button>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
